import { useEffect, useState, useRef } from "react";
import BasicWrapper from "../../components/basicWrapper/basicWrapper";
import ExtendedWrapper from "../../components/extendedWrapper/extendedWrapper";
import { IvnoiceContextProvider } from "./createInvoiceContext";
import { Logtail } from "@logtail/browser";

import { useToaster, Notification, Message } from "rsuite";

import "./steps/createInvoice.css"
//
import InvoiceMain from "./steps/1_invoiceMain";
import InvoiceTariff from "./steps/2_tariffPanel";
import InvoiceAdditions from "./steps/3_invoiceAdditions";
import InvoiceDelivery from "./steps/4_invoiceDelivery";
import InvoiceCalculation from "./steps/5_invoiceCalculation";
import InvoiceResult from "./steps/6_resultPanel";

import InputVoice from "../../components/inputVoice/InputVoice";

import validateForm from "./steps/validators/validatorHelper";

import { useInvoiceContext } from "./createInvoiceContext";
import { useMessenger } from "../../context/messengerContext";
import { buildUserIdPayload } from "../../utils/userIdPayload";
import applyVoice from "./steps/utils/applyVoice";
import applyInvoiceData from "./steps/utils/invoiceStateHelper";
import { cloneDeep, isEqual } from 'lodash';
import calculateTotal from "./steps/utils/calculateTotal";
import { useTranslation } from "react-i18next";

const logtail = new Logtail("aWiRA4tki7wdzxFFFkqxqYKj", {
    endpoint: 'https://in.logs.betterstack.com',
  });

// Helper function to handle invoice data changes and autocorrection
const handleInvoiceDataChanges = (changes, currentData, previousData, isProcessingVoice = false) => {
    if (!changes || Object.keys(changes).length === 0) return currentData;
    
    const updatedData = { ...currentData };
    let hasChanges = false;

    // Check if additions are being explicitly set in this change batch (from template or user)
    const additionsBeingSet = 'additions' in changes;
    const isApplyingTemplate = currentData._isApplyingTemplate;
    const additionsFromTemplate = currentData._additionsFromTemplate;

    // 1. City Changes
    if ('cityFrom' in changes) {
        hasChanges = true;
        // Only reset addressFrom if not applying a template (template application should preserve addressFrom)
        if (currentData.addressFrom && !isApplyingTemplate) updatedData.addressFrom = null;
        
        // Don't clear additions if they're being set in this batch, during voice processing, template application, or if they came from template
        if (!isProcessingVoice && !isApplyingTemplate && !additionsBeingSet && !additionsFromTemplate && currentData.additions && Object.keys(currentData.additions).length) {
            updatedData.additions = {};
        }
    }

    if ('cityTo' in changes) {
        hasChanges = true;
        // Only reset address/pvz fields if not applying a template
        if (currentData.addressTo && !isApplyingTemplate) updatedData.addressTo = null;
        if (currentData.pvzTo && !isApplyingTemplate) updatedData.pvzTo = null;
        
        // Don't clear additions if they're being set in this batch, during voice processing, template application, or if they came from template
        if (!isProcessingVoice && !isApplyingTemplate && !additionsBeingSet && !additionsFromTemplate && currentData.additions && Object.keys(currentData.additions).length) {
            updatedData.additions = {};
        }
    }

    // 2. Places Changes
    if ('places' in changes) {
        hasChanges = true;
        // Don't clear additions if they're being set in this batch, during voice processing, template application, or if they came from template
        if (!isProcessingVoice && !isApplyingTemplate && !additionsBeingSet && !additionsFromTemplate && currentData.additions && Object.keys(currentData.additions).length) {
            updatedData.additions = {};
        }
    }

    // 3. Tariff Changes
    if ('tariff' in changes || 'selected_mode' in changes) {
        hasChanges = true;
        // Handle delivery mode dependent fields
        const newMode = changes.selected_mode || currentData.selected_mode;
        if (newMode) {
            // Reset address fields based on new delivery mode
            if (![1, 2].includes(newMode) && currentData.addressFrom) updatedData.addressFrom = null;
            if (![1, 3].includes(newMode) && currentData.addressTo) updatedData.addressTo = null;
            if (![2, 4].includes(newMode) && currentData.pvzTo) updatedData.pvzTo = null;
        }
        
        // Don't clear additions if they're being set in this batch, during voice processing, template application, or if they came from template
        if (!isProcessingVoice && !isApplyingTemplate && !additionsBeingSet && !additionsFromTemplate && currentData.additions && Object.keys(currentData.additions).length) {
            updatedData.additions = {};
        }
    }

    // 4. Payment Settings - REMOVED OLD LOGIC
    // This logic is now handled by isPaymentOnReceiptAllowed() in 1_invoiceMain.jsx
    // The old logic here was conflicting with the new rules

    // 5. Country Code Effects - DISABLED FOR EAEU
    // EAEU effect in 1_invoiceMain.jsx now handles article/name defaults
    // This old logic was conflicting with EAEU defaults
    /*
    if ('cityFromCountryCode' in changes || 'cityToCountryCode' in changes) {
        hasChanges = true;
        // Handle article field visibility
        if (currentData.cityFromCountryCode !== 1 || currentData.cityToCountryCode !== 1) {
            updatedData.places = updatedData.places.map(place => ({
                ...place,
                goods: place.goods.map(good => ({
                    ...good,
                    article: null
                }))
            }));
        }
    }
    */

    // 6. Delivery Time Changes
    if ('delivery_time_from' in changes) {
        hasChanges = true;
        if (currentData.delivery_time_to && 
            currentData.delivery_time_from?.getHours() >= currentData.delivery_time_to?.getHours()) {
            updatedData.delivery_time_to = null;
        }
        if (currentData.break_time_from && 
            currentData.delivery_time_from?.getHours() >= currentData.break_time_from?.getHours()) {
            updatedData.break_time_from = null;
            updatedData.break_time_to = null;
        }
    }

    return hasChanges ? updatedData : currentData;
};

export default function CreateInvoiceWrapperExtended({
    invoiceStep, setInvoiceStep,
    maxStep, setMaxStep,
    skipCalc, setSkipCalc
}) {
    const { t } = useTranslation('basic');
    const [previousInvoiceData, setPreviousInvoiceData] = useState({});
    const { invoiceData, setInvoiceData } = useInvoiceContext();
    const [prevInvoiceData, setPrevInvoiceData] = useState({});
    const [ voiceData, setVoiceData ] = useState({});
    const [ refresh, setRefresh ] = useState(0);
    const [startTime, setStartTime] = useState(new Date());

    const { userId, platform } = useMessenger();
    const toaster = useToaster();

    const prevInvoiceDataRef = useRef();
    const timeoutRef = useRef(); // Create a ref to hold the timeout ID

    useEffect(() => {
        console.log(voiceData)
        if (!voiceData || !Object.keys(voiceData).length) return;
        setInvoiceData(d => applyVoice(d, voiceData));
        setRefresh(r => r + 1);
    }, [voiceData]);
    
    useEffect(() => {
        // Set yaParams
        window.ym(100750465, 'userParams', {
            ...buildUserIdPayload(platform, userId),
            contract_number: invoiceData?.contract_number,
            UserID: 12345,
        });

        if (prevInvoiceDataRef.current && invoiceData) {
            const changes = Object.keys(invoiceData).reduce((acc, key) => {
                if (!isEqual(invoiceData[key], prevInvoiceDataRef.current[key])) {
                    acc[key] = invoiceData[key];
                }
                return acc;
            }, {});

            console.log(Object.keys(changes).length ? 'Changes detected:' : 'No changes detected', changes);

            // Apply autocorrection if there are changes
            if (Object.keys(changes).length > 0) {
                const updatedData = handleInvoiceDataChanges(changes, invoiceData, prevInvoiceDataRef.current, Object.keys(voiceData).length > 0);
                if (!isEqual(updatedData, invoiceData)) {
                    console.log('Applying autocorrections:', updatedData);
                    setInvoiceData(updatedData);
                    return; // Skip the rest of the effect if we're updating
                }
            }

            // Clear previous timeout and set a new one
            clearTimeout(timeoutRef.current);
            timeoutRef.current = setTimeout(() => {
                logtail.info(
                    `Update with ${userId} and ${invoiceData.contract_number} at ${window.location.pathname} and step ${invoiceStep}`,
                    { changedData: changes }
                );
            }, 300);
        }

        // Update previous invoice data reference
        prevInvoiceDataRef.current = cloneDeep(invoiceData);

        // Cleanup timeout on unmount or dependency change
        return () => clearTimeout(timeoutRef.current);
    }, [invoiceData]);

    return (
        <>
            <ExtendedWrapper>
                {   
                    invoiceStep === 0 && <InvoiceMain /> ||
                    invoiceStep === 1 && <InvoiceTariff /> ||
                    invoiceStep === 2 && <InvoiceAdditions /> ||
                    invoiceStep === 3 && <InvoiceDelivery /> ||
                    invoiceStep === 4 && <InvoiceCalculation /> ||
                    invoiceStep === 5 && <InvoiceResult startTime={startTime} />
                }
                {
                    invoiceStep != 5 ?
                    <InputVoice onNavigateForward={() => {
                        let errors = validateForm(invoiceStep, invoiceData);
                        
                        setInvoiceData({...invoiceData, errors});

                        if (Object.keys(errors).length) {
                            toaster.push(
                            <Message type="error" showIcon>
                                {invoiceStep === 1 ? t("tariff_not_selected") : t("not_all_required_fields_filled")}
                            </Message>, { placement: "topCenter", duration: 3000 });

                            if (errors.sum_overflow) {
                                toaster.push(
                                <Message type="error" showIcon>
                                    {t("insurance_sum_limit")}
                                </Message>, { placement: "topCenter", duration: 3000 });
                            }

                            // check for places.# in keys (# is index)
                            for (let key in errors) {
                                if (key.includes("places")) {
                                    if (errors[key].length == "place_length_invalid_error" ||
                                        errors[key].height == "place_height_invalid_error" ||
                                        errors[key].width == "place_width_invalid_error"
                                    ) {
                                        toaster.push(
                                        <Message type="error" showIcon>
                                            Размер места #{parseInt(key.split('.')[1]) + 1} выходит за допустимые пределы (0.01см - 1000см)
                                        </Message>, { placement: "topCenter", duration: 3000 });
                                    }
                                }
                            }
                            return;
                        };

                        window.ym(100750465, "reachGoal", "clickButton-invoice");

                        if (invoiceData.user_info.contract_type === 3 && invoiceStep === 3) {
                            const calculationResult = calculateTotal(invoiceData);
                            setInvoiceData(oldData => ({...oldData, ...calculationResult}));
                            setInvoiceStep(5);
                            setMaxStep(5);
                            setSkipCalc(true);
                        } else {
                            setInvoiceStep(Math.min(5, invoiceStep + 1));
                        }
                    }} setRefresh={setRefresh} ml_page={"prepare_invoice_full"} setJsonForm={setVoiceData}/>
                    : null
                }
            </ExtendedWrapper>
        </>
    )
}